import React from 'react';
import { useParams } from 'react-router';

import { Alert } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { CUSTOMER_TICKET } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { History__Subject, Ticket__TicketableEnum, useLeadQuery, Lead } from '@admin/schema';

import { DeleteLeadButton } from '../delete_lead_button';
import { HistoriesPanel } from '../histories/panel';
import { MessagesWidget } from '../messages/widget';
import { CustomerTicketPanel } from '../shared/customer_tickets_panel';
import { TicketsPanel } from '../tickets/panel';
import { CreateTicket as CreateTechSupportTicket } from '../tickets/tech_support/create_ticket';
import { LeadEditForm } from './edit_form';

export const LeadEditPage: React.FC = () => {
  useTitle('Edit Lead');
  const { id } = useParams<{ id: string }>();

  const {
    data: leadData,
    loading,
    error,
  } = useLeadQuery({
    variables: { id: id },
    client,
  });

  const lead = !loading && leadData?.lead;

  if (error) {
    return <Alert style="danger">{error.message}</Alert>;
  }

  // Handle not found case
  if (!loading && !leadData?.lead) {
    return (
      <div>
        <Alert style="danger">
          Lead not found. The lead may have been deleted or you may not have permission to view it.
          <br />
          <a href="/leads" className="btn btn-primary" style={{ marginTop: '10px' }}>
            Back to Leads List
          </a>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="lead-form-container">
        <div className="panel">
          <div className="panel-body">
            <div className="form-group text-right">
              <div className="pull-left">
                <DeleteLeadButton lead={{ id: Number(id) }} data-qa="delete-lead-button" />
              </div>
              <div>
                <CreateTechSupportTicket
                  ticketable={{
                    id,
                    type: Ticket__TicketableEnum.Lead,
                    displayName: `Lead ${id}`,
                  }}
                />
              </div>
            </div>
            {lead ? <LeadEditForm lead={lead} /> : <Spinner />}
          </div>
        </div>
      </div>

      {lead && (
        <div>
          <Permission resource={CUSTOMER_TICKET.resource} action={CUSTOMER_TICKET.actions.read} denied={null}>
            <CustomerTicketPanel customerTicketableID={id} customerTicketableType="Lead" />
          </Permission>
          <TicketsPanel ticketable={{ id, type: Ticket__TicketableEnum.Lead }} />
          <MessagesWidget recipientType="Lead" recipient={lead} />
          <HistoriesPanel type={History__Subject.Lead} id={id} />
        </div>
      )}
    </div>
  );
};
